/**
 * Admin Users Page
 * 
 * Main admin page for managing users, roles, and permissions.
 * Provides a comprehensive user management experience with user list, role assignment, and permissions editing.
 */


import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/shared/PageLayout';
import { useToast } from '@/hooks/use-toast';
import { UserRole } from '@/components/authnz/types/rbac';
import { useAdminAccessGate } from '@/contexts/use-admin-access-gate';
import { UsersAndRolesTab } from './UsersAndRolesTab';
import {
  isUsersRolesPrototypeVariant,
  UsersRolesPrototypeHost,
} from './_prototype/users-roles/UsersRolesPrototypeHost';

export function AdminUsersPage() {
  const { user: currentUser, serverUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showPrototype =
    process.env.NODE_ENV !== 'production' &&
    isUsersRolesPrototypeVariant(searchParams.get('variant'));
  const { toast } = useToast();
  const {
    isReady,
    isAuthorizedAdmin,
    isAdminModeEnabled,
    hasAdminAccess,
  } = useAdminAccessGate();
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (!currentUser || !serverUser) {
      navigate('/login');
      setHasAccess(false);
      setIsChecking(false);
      return;
    }

    const isAdmin = serverUser.roles?.includes(UserRole.ADMINISTRATOR);
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'You must be an administrator to access this page.',
        variant: 'destructive',
      });
      navigate('/');
      setHasAccess(false);
    } else if (!isAdminModeEnabled || !isAuthorizedAdmin) {
      toast({
        title: 'Admin Mode Required',
        description: 'Please enable Admin Mode in Settings to access this page.',
        variant: 'destructive',
      });
      navigate('/settings');
      setHasAccess(false);
    } else {
      setHasAccess(true);
    }

    setIsChecking(false);
  }, [
    currentUser,
    serverUser,
    isReady,
    isAdminModeEnabled,
    isAuthorizedAdmin,
    hasAdminAccess,
    navigate,
    toast,
  ]);

  if (isChecking || !isReady) {
    return (
      <PageLayout title="User Management" isLoading={true}>
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </PageLayout>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <PageLayout
      title="User Management"
      subtitle="Manage users and assign roles for the application"
    >
      <div className="mt-2">
        {showPrototype ? <UsersRolesPrototypeHost /> : <UsersAndRolesTab />}
      </div>
    </PageLayout>
  );
}
